/**
 * Author: Romain Hapi, Prenim Ltd
 * Version: 1.0
 * Date: 2026-10-22
 * Project: Prenim Digital
 * Copyright: Prenim Ltd
 */
/**
 * Page detaillee d'un logiciel Prenim : fonctionnalites, captures,
 * modes d'installation et documents, en francais ou en anglais.
 *
 * Comme pour les services, un slug inconnu renvoie vers les realisations
 * plutot que d'afficher une page vide.
 */
import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import CalendlyButton from "../components/CalendlyButton";
import { ArrowLeft, ArrowRight, CheckCircle2, Download, ExternalLink, Mail, Phone, Globe, MessageCircle } from "lucide-react";
import { PRODUCT_UI, contentLang, findProduct, tx } from "../lib/products";

/** Source d'une image ou d'un document, commune ou propre a chaque langue. */
const pick = (value, l) => (typeof value === "string" ? value : value[l]);

const ProductDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  if (!t) return null;

  const product = findProduct(slug);
  if (!product) return <Navigate to="/projects" replace />;

  const l = contentLang(lang);
  const ui = (key) => tx(PRODUCT_UI[key], lang);
  const theme = product.theme || { from: "#1e3a8a", to: "#0f172a", accent: "#fbbf24" };
  const brochure = product.documents[product.documents.length - 1];
  const mail = (key) =>
    `mailto:hello@prenim.co.uk?subject=${encodeURIComponent(`${ui(key)} : ${product.name}`)}`;
  const whatsapp = (number) =>
    `https://wa.me/${number}?text=${encodeURIComponent(`${ui("ctaDemo")} : ${product.name}`)}`;

  // Bande d'appels a l'action repetee dans la page : demo, devis, WhatsApp
  // (Tchad et Cameroun seulement) et plaquette quand elle existe.
  const ctaStrip = (dark) => (
    <div className="flex flex-wrap gap-3">
      <a href={mail("ctaDemo")} className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-5 py-3 transition-colors">
        <Mail className="h-4 w-4" /> {ui("ctaDemo")}
      </a>
      <a href={mail("ctaQuote")} className={`inline-flex items-center gap-2 rounded-full border-2 font-semibold px-5 py-3 transition-colors ${dark ? "border-white/30 text-white hover:bg-white hover:text-slate-950" : "border-slate-300 text-slate-900 hover:border-blue-500 hover:text-blue-700"}`}>
        {ui("ctaQuote")} <ArrowRight className="h-4 w-4 rtl-flip" />
      </a>
      <a href={whatsapp("23585206761")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-5 py-3 transition-colors">
        <MessageCircle className="h-4 w-4" /> {ui("ctaWhatsappTd")}
      </a>
      <a href={whatsapp("237655655172")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-5 py-3 transition-colors">
        <MessageCircle className="h-4 w-4" /> {ui("ctaWhatsappCm")}
      </a>
      {brochure && (
        <a href={pick(brochure.href, l)} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 rounded-full font-semibold px-5 py-3 transition-colors ${dark ? "text-slate-200 hover:text-white" : "text-slate-700 hover:text-blue-700"}`}>
          <Download className="h-4 w-4" /> {ui("ctaBrochure")}
        </a>
      )}
    </div>
  );

  return (
    <main data-testid={`product-detail-${slug}`} className="pt-20">
      <SEO title={`${product.name} - Prenim`} description={tx(product.summary, lang)} lang={lang} />

      {/* En-tete */}
      <section className="bg-slate-950 text-white py-20 lg:py-28">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-cyan-400 mb-8 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 rtl-flip" />
              {ui("back")}
            </Link>
            <span className="block text-xs font-semibold uppercase tracking-widest text-cyan-400">
              {tx(product.category, lang)}
            </span>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              {product.name}
            </h1>
            <p className="mt-4 text-xl text-slate-200 font-medium">{tx(product.tagline, lang)}</p>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">{tx(product.summary, lang)}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {product.demoUrl && (
                <a
                  href={product.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 transition-colors"
                >
                  {ui("demo")}
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              {product.externalUrl && (
                <a
                  href={product.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 transition-colors"
                >
                  {product.externalUrl.replace(/^https?:\/\/(www\.)?/, "")}
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              <a
                href={mail("ctaDemo")}
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 hover:bg-white hover:text-slate-950 font-semibold px-6 py-3 transition-colors"
              >
                {ui("ctaDemo")}
                <ArrowRight className="h-4 w-4 rtl-flip" />
              </a>
              <a
                href={whatsapp("23585206761")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-500 font-semibold px-6 py-3 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          </div>
          <div className="flex justify-center">
            <img
              src={pick(product.cover, l)}
              alt={product.name}
              className="max-h-[480px] w-auto object-contain rounded-2xl"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Chiffres */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {product.stats.map((s, i) => (
            <div key={i} className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-blue-600">{s.value}</div>
              <div className="mt-1 text-sm text-slate-600">{tx(s.label, lang)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Fonctionnalites */}
      <section data-testid="product-features" className="py-20 lg:py-24">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-950 mb-12">{ui("features")}</h2>
          {/* Cartes aux couleurs du produit (theme de products.js) : degrade de
              la teinte de marque vers sa nuance sombre, accent pour l'icone et
              le numero. Une legere lueur en haut a droite donne du relief. */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.features.map((f, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-2xl p-7 text-white shadow-lg shadow-slate-900/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                style={{ background: `linear-gradient(145deg, ${theme.from} 0%, ${theme.to} 100%)` }}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-16 -end-16 h-44 w-44 rounded-full opacity-25 blur-2xl transition-opacity duration-300 group-hover:opacity-40"
                  style={{ background: theme.accent }}
                />
                <div className="relative flex items-start justify-between mb-5">
                  <div
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl ring-1 ring-white/20"
                    style={{ background: `${theme.accent}26`, color: theme.accent }}
                  >
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <span className="text-3xl font-bold leading-none text-white/15 select-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="relative text-lg font-semibold mb-2" style={{ color: theme.accent }}>
                  {tx(f.title, lang)}
                </h3>
                <p className="relative text-white/85 leading-relaxed text-sm">{tx(f.body, lang)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Appel a l'action apres les fonctionnalites */}
      <section className="py-16 bg-slate-950 text-white">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold">{ui("stripTitle")}</h2>
          <p className="mt-3 text-slate-300 max-w-3xl">{ui("stripBody")}</p>
          <div className="mt-8">{ctaStrip(true)}</div>
        </div>
      </section>

      {/* Captures */}
      {product.gallery.length > 0 && (
        <section data-testid="product-gallery" className="py-20 lg:py-24">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-950">{ui("gallery")}</h2>
            {product.slug !== "fadel" && <p className="mt-3 text-sm text-slate-500">{ui("galleryNote")}</p>}
            <div
              className={`mt-10 grid gap-6 ${
                product.gallery[0].portrait
                  ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
                  : "sm:grid-cols-2 lg:grid-cols-3"
              }`}
            >
              {product.gallery.map((g, i) => {
                const src = pick(g.src, l);
                return (
                  <figure key={i}>
                    <a href={src} target="_blank" rel="noopener noreferrer" className="block">
                      <img
                        src={src}
                        alt={tx(g.caption, lang)}
                        loading="lazy"
                        className={`w-full rounded-xl border border-slate-200 bg-slate-50 object-cover object-top hover:shadow-lg transition-shadow ${
                          g.portrait ? "aspect-[9/19]" : "aspect-[16/10]"
                        }`}
                      />
                    </a>
                    <figcaption className="mt-2 text-sm text-slate-600">{tx(g.caption, lang)}</figcaption>
                  </figure>
                );
              })}
            </div>
            <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-8">
              <p className="text-xl font-semibold text-slate-950 mb-6">{ui("galleryCta")}</p>
              {ctaStrip(false)}
            </div>
          </div>
        </section>
      )}

      {/* Installation */}
      {product.deployment.length > 0 && (
        <section data-testid="product-deployment" className="py-20 lg:py-24 bg-slate-50">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-950 mb-12">{ui("deployment")}</h2>
            <div className={`grid gap-6 md:grid-cols-2 ${product.deployment.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
              {product.deployment.map((d, i) => (
                <div key={i} className="relative">
                  <div className="text-6xl font-bold text-slate-200 absolute -top-2 -start-1 select-none">0{i + 1}</div>
                  <div className="relative pt-6">
                    <h3 className="text-xl font-semibold text-slate-950 mb-2">{tx(d.title, lang)}</h3>
                    <p className="text-slate-600 leading-relaxed">{tx(d.body, lang)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Documents */}
      {product.documents.length > 0 && (
        <section data-testid="product-documents" className="py-16">
          <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-950 mb-8">{ui("documents")}</h2>
            <div className="flex flex-wrap gap-4">
              {product.documents.map((d, i) => (
                <a
                  key={i}
                  href={pick(d.href, l)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 font-medium text-slate-800 hover:border-blue-400 hover:text-blue-700 transition-colors"
                >
                  <Download className="h-5 w-5" />
                  {tx(d.label, lang)}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact : chaque canal nomme, avec son usage */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold">{ui("contactTitle")}</h2>
          <p className="mt-4 text-blue-100 text-lg leading-relaxed">{ui("contactBody")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-blue-50">
            <a href="mailto:hello@prenim.co.uk" className="inline-flex items-center gap-2 hover:underline">
              <Mail className="h-4 w-4" /> hello@prenim.co.uk
            </a>
            <a href="tel:+23585206761" className="inline-flex items-center gap-2 hover:underline">
              <Phone className="h-4 w-4" /> +235 85 20 67 61
            </a>
            <a href="tel:+237655655172" className="inline-flex items-center gap-2 hover:underline">
              <Phone className="h-4 w-4" /> +237 6 55 65 51 72
            </a>
            <a href="tel:+441138805181" className="inline-flex items-center gap-2 hover:underline">
              <Phone className="h-4 w-4" /> +44 0113 880 5181
            </a>
            <span className="inline-flex items-center gap-2">
              <Globe className="h-4 w-4" /> prenim.co.uk
            </span>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <CalendlyButton variant="primary" testId={`product-${slug}-calendly`}>
              {t.common.book_consultation}
            </CalendlyButton>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 hover:bg-white text-white hover:text-blue-700 font-semibold px-6 py-3 transition-colors"
            >
              {ui("contactCta")}
              <ArrowRight className="h-4 w-4 rtl-flip" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
